import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";
import { useT } from "../../i18n";

const HERO_IMG =
  "https://images.unsplash.com/photo-1678838032358-d5ef71b283db?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Hero() {
  const { t } = useT();
  const lines = t("hero.titleLines") || [];
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const [live, setLive] = useState({ p: 42, i: 6800, c: 24 });
  useEffect(() => {
    const id = setInterval(() => {
      setLive((v) => ({ ...v, i: v.i + Math.floor(Math.random() * 3) }));
    }, 4000);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden pt-16 pb-24 md:pt-24 md:pb-32"
      data-testid="hero-section"
      id="top"
    >
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14">
        <motion.div style={{ y: textY }}>
          <div className="flex items-center gap-3 mb-10">
            <span className="eyebrow">{t("hero.eyebrow")}</span>
            <span className="text-[0.7rem] font-mono text-[#7a857c]">ARG · 2026</span>
          </div>

          <h1 className="font-serif text-[#1a211c] leading-[0.98] tracking-[-0.02em] text-[clamp(2.5rem,7vw,6.4rem)] max-w-[16ch]">
            {lines.map((ln, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  initial={{ y: "110%", opacity: 0 }}
                  animate={{ y: "0%", opacity: 1 }}
                  transition={{
                    duration: 1.05,
                    ease: [0.22, 1, 0.36, 1],
                    delay: 0.15 + i * 0.14,
                  }}
                >
                  {ln}{" "}
                  {i === lines.length - 1 && (
                    <span
                      className="italic font-normal text-[#3d4f42]"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    ></span>
                  )}
                </motion.span>
              </span>
            ))}
          </h1>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-end">
          <div className="lg:col-span-5">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.8 }}
              className="text-[1.05rem] md:text-[1.15rem] leading-relaxed text-[#1a211c]/75 max-w-xl"
            >
              {t("hero.subtitle")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.8 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <a href="#projects" className="btn-olive" data-testid="hero-cta-primary">
                {t("hero.primaryCta")}
                <ArrowUpRight size={16} />
              </a>
              <a href="#how-it-works" className="btn-ghost" data-testid="hero-cta-secondary">
                {t("hero.secondaryCta")}
                <ArrowDownRight size={16} />
              </a>
            </motion.div>
          </div>

          <div className="lg:col-span-7 relative">
            <motion.div
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
              className="relative aspect-[4/5] md:aspect-[16/12] rounded-[6px] overflow-hidden bg-[#1a211c] clip-arch"
            >
              <motion.img
                src={HERO_IMG}
                alt="Buenos Aires residential skyline"
                loading="eager"
                style={{ y: imageY, scale: imageScale }}
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 hero-spot" />
              <div className="absolute inset-0 p-6 md:p-9 flex flex-col justify-between text-[#f6f4ee]">
                <div className="flex items-center justify-between">
                  <span className="text-[0.7rem] font-mono tracking-[0.28em] text-[#c5b293]">
                    ARG · BUENOS AIRES
                  </span>
                  <span className="text-[0.7rem] font-mono tracking-[0.28em] text-[#c5b293]">
                    34.60°S · 58.38°W
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-[#f6f4ee]/15">
                  <Stat
                    label={t("hero.stats.projectsLabel")}
                    value={live.p}
                    testid="hero-stat-projects"
                  />
                  <Stat
                    label={t("hero.stats.investorsLabel")}
                    value={`${(live.i / 1000).toFixed(1)}k`}
                    testid="hero-stat-investors"
                  />
                  <Stat
                    label={t("hero.stats.capitalLabel")}
                    value={`USD ${live.c}M`}
                    testid="hero-stat-capital"
                  />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4, duration: 0.8 }}
              className="hidden lg:flex absolute -left-8 bottom-6 items-center gap-3 rotate-[-90deg] origin-left"
            >
              <span className="h-px w-10 bg-[#1a211c]/30" />
              <span className="text-[0.7rem] font-mono tracking-[0.3em] text-[#1a211c]/60 uppercase">
                {t("hero.captionScroll")}
              </span>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="font-serif text-3xl md:text-4xl tabular text-[#f6f4ee]">{value}</div>
      <div className="mt-1 text-[0.7rem] uppercase tracking-[0.2em] text-[#f6f4ee]/60">{label}</div>
    </div>
  );
}
